import { computed, Injectable, signal } from '@angular/core';
import { CrmObjectInterface } from '@vendasta/crm';
import { ObjectType } from '../tokens-and-interfaces';
import { SystemFieldIds } from './crm-services';

@Injectable({ providedIn: 'root' })
export class CrmObjectManagementTableChangesService {
  private createdCrmObjects = signal(new Map<string, CrmObjectInterface[]>());
  private updatedCrmObjects = signal(new Map<string, CrmObjectInterface[]>());
  private deletedCrmObjectIds = signal(new Map<string, string[]>());
  private changedCrmObjects = computed(() => {
    return {
      created: this.createdCrmObjects(),
      updated: this.updatedCrmObjects(),
      deleted: this.deletedCrmObjectIds(),
    };
  });

  getCrmObjectChanges() {
    return this.changedCrmObjects;
  }

  clearAllChanges(objectType?: ObjectType, objectSubtype?: string): void {
    const type = objectSubtype || objectType || null;
    this.clearCreatedCrmObjects(type);
    this.clearUpdatedCrmObjects(type);
    this.clearDeletedCrmObjectIds(type);
  }

  addCreatedCrmObject(objectType: ObjectType, crmObject: CrmObjectInterface, objectSubtype?: string): void {
    if (!crmObject) {
      return;
    }
    this.createDatesForPreview(objectType, crmObject);
    const type = objectSubtype || objectType;
    this.createdCrmObjects.update((createdCrmObjectsMap) => {
      const createdCrmObjects = createdCrmObjectsMap.get(type) || [];
      createdCrmObjects.push(crmObject);
      createdCrmObjectsMap.set(type, createdCrmObjects);
      return new Map(createdCrmObjectsMap);
    });
  }

  private clearCreatedCrmObjects(type: string | null): void {
    if (type) {
      this.createdCrmObjects.update((createdCrmObjectsMap) => {
        createdCrmObjectsMap.delete(type);
        return new Map(createdCrmObjectsMap);
      });
    } else {
      this.createdCrmObjects.set(new Map([]));
    }
  }

  addUpdatedCrmObject(objectType: ObjectType, crmObject: CrmObjectInterface, objectSubtype?: string): void {
    if (!crmObject) {
      return;
    }
    this.createDatesForPreview(objectType, crmObject);
    const type = objectSubtype || objectType;
    this.updatedCrmObjects.update((changedCrmObjectsMap) => {
      const changedCrmObjects = changedCrmObjectsMap.get(type) || [];
      const existingIndex = changedCrmObjects.findIndex((obj) => obj.crmObjectId === crmObject.crmObjectId);
      if (existingIndex !== -1) {
        changedCrmObjects[existingIndex] = crmObject;
      } else {
        changedCrmObjects.push(crmObject);
      }
      changedCrmObjectsMap.set(type, [...changedCrmObjects]);
      return new Map(changedCrmObjectsMap);
    });
  }

  private clearUpdatedCrmObjects(type: string | null): void {
    if (type) {
      this.updatedCrmObjects.update((updatedCrmObjectsMap) => {
        updatedCrmObjectsMap.delete(type);
        return new Map(updatedCrmObjectsMap);
      });
    } else {
      this.updatedCrmObjects.set(new Map());
    }
  }

  addDeletedCrmObjectId(objectType: ObjectType, crmObjectId: string, objectSubtype?: string): void {
    if (!crmObjectId) {
      return;
    }
    const type = objectSubtype || objectType;
    this.deletedCrmObjectIds.update((deletedCrmObjectIdsMap) => {
      const deletedCrmObjectIds = deletedCrmObjectIdsMap.get(type) || [];
      if (!deletedCrmObjectIds.includes(crmObjectId)) {
        deletedCrmObjectIdsMap.set(type, [...deletedCrmObjectIds, crmObjectId]);
      }
      return new Map(deletedCrmObjectIdsMap);
    });
  }

  private clearDeletedCrmObjectIds(type: string | null): void {
    if (type) {
      this.deletedCrmObjectIds.update((deletedCrmObjectIdsMap) => {
        deletedCrmObjectIdsMap.delete(type);
        return new Map(deletedCrmObjectIdsMap);
      });
    } else {
      this.deletedCrmObjectIds.set(new Map([]));
    }
  }

  private createDatesForPreview(objectType: ObjectType, crmObject: CrmObjectInterface): void {
    let objectCreatedFieldId: string;
    let objectUpdatedFieldId: string;
    switch (objectType) {
      case 'Contact':
        objectCreatedFieldId = SystemFieldIds.ContactCreated;
        objectUpdatedFieldId = SystemFieldIds.ContactUpdated;
        break;
      case 'Company':
        objectCreatedFieldId = SystemFieldIds.CompanyCreated;
        objectUpdatedFieldId = SystemFieldIds.CompanyUpdated;
        break;
      case 'Activity':
        objectCreatedFieldId = SystemFieldIds.ActivityCreated;
        objectUpdatedFieldId = SystemFieldIds.ActivityUpdated;
        break;
      case 'Opportunity':
        objectCreatedFieldId = SystemFieldIds.OpportunityCreated;
        objectUpdatedFieldId = SystemFieldIds.OpportunityUpdated;
        break;
      case 'CustomObject':
        objectCreatedFieldId = SystemFieldIds.CustomObjectCreated;
        objectUpdatedFieldId = SystemFieldIds.CustomObjectUpdated;
        break;
    }
    const changeTimestamp = new Date();
    // guarantee preview object will show proper dates for updated/created columns
    const createdDateIndex = crmObject.fields?.findIndex((f) => f.fieldId === objectCreatedFieldId) || -1;
    const updatedDateIndex = crmObject.fields?.findIndex((f) => f.fieldId === objectUpdatedFieldId) || -1;
    if (createdDateIndex !== -1 && !!crmObject?.fields && crmObject?.fields[createdDateIndex]?.dateValue === null) {
      crmObject.fields[createdDateIndex].dateValue = changeTimestamp;
      crmObject.created = changeTimestamp;
    } else if (createdDateIndex === -1 && !!crmObject?.fields) {
      crmObject.fields.push({
        fieldId: objectCreatedFieldId,
        externalId: objectCreatedFieldId,
        dateValue: changeTimestamp,
      });
      crmObject.created = changeTimestamp;
    }
    if (updatedDateIndex !== -1 && !!crmObject?.fields) {
      crmObject.fields[updatedDateIndex].dateValue = changeTimestamp;
      crmObject.updated = changeTimestamp;
    } else if (updatedDateIndex === -1 && !!crmObject?.fields) {
      crmObject.fields.push({
        fieldId: objectUpdatedFieldId,
        externalId: objectUpdatedFieldId,
        dateValue: changeTimestamp,
      });
      crmObject.updated = changeTimestamp;
    }
  }
}
